import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { 
  ArrowLeft, Globe, RefreshCcw, Layout, ShoppingCart, 
  Bell, CheckCircle2, AlertTriangle, ExternalLink, Copy
} from "lucide-react";
import { getCompetitorAds, listCompetitors, resyncCompetitorAds } from "@/lib/api";

export default function CompetitorDetailPage() {
  const { idSlug } = useParams();
  const id = idSlug?.split("-")[0];
  const [activeTab, setActiveTab] = useState("ads");
  const [competitor, setCompetitor] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await listCompetitors();
        const all = response?.success ? response.data.competitors || [] : [];
        const found = all.find((c) => String(c.id) === String(id));
        
        if (found) {
          setCompetitor(found);
          // Backend now includes ads and products in the detail load
          setAds(found.ads || []);
        }
      } catch (e) {
        setError("Could not load competitor intelligence.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleCopyStack = () => {
    const stack = competitor?.match_logic?.techStack;
    if (stack) {
      const text = `Theme: ${stack.theme}\nApps: ${stack.apps?.join(", ")}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Performing deep scan...</div>;
  if (!competitor) return <div className="p-10 text-center text-red-500">Competitor not found.</div>;

  const techStack = competitor.match_logic?.techStack;
  const products = competitor.products || [];
  const oosCount = products.filter(p => p.status === "out_of_stock").length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <Link to="/dashboard/competitors" className="flex items-center text-sm text-gray-500 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Intelligence Hub
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{competitor.brand_name}</h1>
            <Badge variant={competitor.sync_status === "complete" ? "success" : "secondary"}>
              {competitor.sync_status === "complete" ? "VERIFIED" : "SYNCING"}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" /> {competitor.domain}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => resyncCompetitorAds(competitor.id)} disabled={resyncing}>
            <RefreshCcw className={`w-4 h-4 mr-2 ${resyncing ? 'animate-spin' : ''}`} />
            Refresh Intel
          </Button>
          <Button variant="default" size="sm" className="bg-primary hover:bg-radar-secondary text-primary-foreground" onClick={() => window.open(`https://${competitor.domain}`, "_blank")}>
            Visit Store <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { id: "ads", label: "Ad Intelligence", icon: Layout },
          { id: "inventory", label: "Inventory Sniper", icon: ShoppingCart, count: oosCount },
          { id: "alerts", label: "Brand Alerts", icon: Bell }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id 
                ? "border-primary text-primary" 
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && <Badge className="bg-red-100 text-red-600 ml-1">{tab.count}</Badge>}
          </button>
        ))}
      </div>

      {/* Tab Content: Ad Intelligence */}
      {activeTab === "ads" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Strategy Pulse</p>
              <p className="text-xl font-bold text-gray-900">Aggressive Scaling</p>
              <Badge className="bg-emerald-50 text-emerald-600 border-emerald-100">High Confidence</Badge>
            </div>
            {techStack && (
              <div className="md:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between gap-6">
                <div className="space-y-4 flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Conversion Blueprint</p>
                    <Button variant="ghost" size="sm" onClick={handleCopyStack} className="h-7 text-xs">
                      {copied ? "Copied!" : <><Copy className="w-3 h-3 mr-1" /> Copy Stack</>}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-primary/10 text-gray-900 border-primary/20">Theme: {techStack.theme}</Badge>
                    {techStack.apps?.map(app => (
                      <Badge key={app} variant="secondary" className="bg-gray-100 text-gray-600">{app}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="w-[100px]">Format</TableHead>
                  <TableHead>Creative Title</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Longevity</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell>
                      <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden">
                        {ad.image_url && <img src={ad.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-gray-900">{ad.ad_creative_title || "Untitled Creative"}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{ad.platform}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-emerald-600">64 days</span>
                        <span className="text-[10px] text-gray-400">WINNING AD</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => window.open(ad.ad_snapshot_url, "_blank")}>
                        View Battle Intel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Tab Content: Inventory Sniper */}
      {activeTab === "inventory" && (
        <div className="space-y-6">
          {oosCount > 0 && (
            <div className="bg-red-50 border border-red-100 p-4 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-900">Blood in the Water Detected</p>
                <p className="text-sm text-red-700">This rival is out of stock on {oosCount} products. Increase your ad spend on similar items now.</p>
              </div>
            </div>
          )}
          
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead>Product</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sales (Recent)</TableHead>
                  <TableHead className="text-right">Est. Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length > 0 ? products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded border border-gray-100 overflow-hidden bg-gray-50">
                        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <span className="font-medium text-sm text-gray-900">{p.title}</span>
                    </TableCell>
                    <TableCell className="text-sm font-bold">${parseFloat(p.price).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === "out_of_stock" ? "destructive" : "success"}>
                        {p.status === "out_of_stock" ? "OUT OF STOCK" : "STABLE"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.recent_sales ? <span className="text-emerald-600 font-bold">{p.recent_sales} units</span> : "Analyzing..."}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {p.recent_sales ? `$${(p.recent_sales * p.price).toLocaleString()}` : "—"}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-gray-400 italic">No products mapped yet. Refresh intel to start sniper.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Tab Content: Brand Alerts */}
      {activeTab === "alerts" && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
          {competitor.alerts?.length > 0 ? competitor.alerts.map(alert => (
            <div key={alert.id} className="p-4 flex items-start gap-4">
              <div className={`p-2 rounded-lg ${alert.type === 'stock_out' ? 'bg-red-50 text-red-600' : 'bg-primary/10 text-primary'}`}>
                {alert.type === 'stock_out' ? <ShoppingCart className="w-4 h-4" /> : <RefreshCcw className="w-4 h-4" />}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-bold text-gray-900">{alert.message}</p>
                <p className="text-xs text-gray-400">{new Date(alert.created_at).toLocaleString()}</p>
              </div>
            </div>
          )) : (
            <div className="p-10 text-center text-gray-400 italic">No historical alerts for this brand.</div>
          )}
        </div>
      )}
    </div>
  );
}
